import { useState } from 'react';
import { Menu, X, Bell, Settings, User, LogOut, TrendingUp, BarChart3, Briefcase, Star, MessageSquare, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface HeaderProps {
  onAuthClick: (mode: 'login' | 'signup') => void;
  currentPage: string;
  onNavigate: (page: string) => void;
  onSettingsClick?: () => void;
  telegramUser?: any;
}

export const Header = ({ onAuthClick, currentPage, onNavigate, onSettingsClick, telegramUser }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();

  const isLoggedIn = !!telegramUser || !!user;

  const handleSignOut = async () => {
    await signOut();
    setMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'market', label: 'Market', icon: TrendingUp },
    { id: 'screener', label: 'Screener', icon: Search },
    { id: 'analysis', label: 'Analysis', icon: BarChart3 },
    { id: 'portfolio', label: 'Portfolio', icon: Briefcase },
    { id: 'watchlist', label: 'Watchlist', icon: Star },
    { id: 'consult', label: 'Consult', icon: MessageSquare },
  ];

  return (
    <header className="sticky top-0 z-50 bg-[#0a0e17]/95 backdrop-blur-md border-b border-slate-800">
      <nav className="max-w-[1600px] mx-auto px-4 lg:px-6">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-8">
            <button onClick={() => onNavigate('market')} className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">
                <span className="text-white">GO</span>
                <span className="gradient-text">TRADINGZY</span>
              </span>
            </button>

            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => onNavigate(item.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      currentPage === item.id
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="hidden lg:flex items-center space-x-3">
            {isLoggedIn ? (
              <>
                <button className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all relative">
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-emerald-500 rounded-full"></span>
                </button>
                <button
                  onClick={onSettingsClick}
                  className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
                >
                  <Settings className="w-5 h-5" />
                </button>
                <div className="h-6 w-px bg-slate-700"></div>
                <div className="flex items-center space-x-3 px-3 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm text-slate-300 max-w-[120px] truncate">
                    {telegramUser?.telegram_username ? `@${telegramUser.telegram_username}` : user?.email || 'User'}
                  </span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="p-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onSettingsClick}
                  className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
                >
                  <Settings className="w-5 h-5" />
                </button>
                <button
                  onClick={() => onAuthClick('login')}
                  className="btn-primary"
                >
                  Masuk
                </button>
              </>
            )}
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-800 animate-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      onNavigate(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${
                      currentPage === item.id
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}

              <div className="pt-4 mt-4 border-t border-slate-800">
                <button
                  onClick={() => {
                    if (onSettingsClick) onSettingsClick();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg"
                >
                  <Settings className="w-5 h-5" />
                  <span>Pengaturan</span>
                </button>

                {isLoggedIn ? (
                  <div className="space-y-3 mt-3">
                    <div className="flex items-center space-x-3 px-4 py-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-sm text-slate-300">
                        {telegramUser?.telegram_username ? `@${telegramUser.telegram_username}` : user?.email || 'User'}
                      </span>
                    </div>
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-500/10 text-red-400 rounded-lg font-medium"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Keluar</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3 px-4 mt-3">
                    <button
                      onClick={() => {
                        onAuthClick('login');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full btn-primary"
                    >
                      Masuk
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
